// Content for the Learning Pathways page (/pathways). Programs are referenced
// by their page path so titles, durations and badges come from the catalog.

export interface IUpcoming {
  name: string;
  note: string; // e.g. "Coming soon in 2027"
}

const DAPDI: IUpcoming = {
  name: "Digital ASIC Physical Design & Implementation (DAPDI)",
  note: "Coming soon in 2027",
};

export interface IStage {
  key: string;
  label: string;
  title: string;
  subtitle: string;
  icon: string;
  color: string;
  points: string[];
  // Catalog programs at this stage (by actionProgramDetails path).
  programs?: string[];
  // Announced programs that don't have a page yet.
  upcoming?: IUpcoming[];
  // Non-program milestones (outcomes, career support).
  milestones?: { icon: string; text: string }[];
}

export const stages: IStage[] = [
  {
    key: "discover",
    label: "Discover",
    title: "Free Workshops",
    subtitle: "Skill Cast",
    icon: "bi-lightbulb",
    color: "#7b5cd6",
    points: [
      "Free, one-day public workshops open to everyone",
      "Spark curiosity in quantum, AI and analog IC design",
      "Slides and notebooks shared on our Resources page",
    ],
    programs: [
      "/introduction-to-quantum-computing",
      "/introduction-to-generative-ai",
      "/analog-cmos-ic-design",
    ],
  },
  {
    key: "foundations",
    label: "Foundations",
    title: "Certificate Bootcamps",
    subtitle: "Skill Camp",
    icon: "bi-bricks",
    color: "#1f9d6b",
    points: [
      "8-week bootcamps with a Skill Surf certificate",
      "No prior experience in the area needed",
      "Strong fundamentals before abstractions",
    ],
    programs: [
      "/fundamentals-of-digital-system-design",
      "/microcontroller-based-embedded-system-design",
      "/cmos-ic-design",
      "/cryptography-fundamentals-securing-the-digital-world",
    ],
  },
  {
    key: "build",
    label: "Build",
    title: "Hands-on Design",
    subtitle: "Skill Camp",
    icon: "bi-tools",
    color: "#f2683a",
    points: [
      "Build real circuits, prototypes and models",
      "Industry tools: OpenLane, Tiny Tapeout and more",
      "Deliver working products, like the smart plugs from EPDIoT '25",
    ],
    programs: [
      "/verilog-for-ASIC-FPGA-design-simulation",
      "/embedded-product-design",
      "/embedded-machine-learning-for-edge-computing",
    ],
  },
  {
    key: "advance",
    label: "Advance",
    title: "Architecture & Systems",
    subtitle: "Skill Camp · Skill Cast",
    icon: "bi-cpu",
    color: "#0089e5",
    points: [
      "Design, build and test a RISC-V CPU in SystemVerilog",
      "System-on-chip design on FPGA with ARM and RISC-V",
      "Physical design from netlist to GDSII",
    ],
    programs: ["/hicar", "/system-on-a-chip-design-workshop"],
    upcoming: [DAPDI],
  },
  {
    key: "compete",
    label: "Compete",
    title: "Competitions, Research & Olympiads",
    subtitle: "Recognition beyond the classroom",
    icon: "bi-trophy",
    color: "#d9a21b",
    points: [
      "Compete in national and international design contests",
      "Turn projects into research publications",
      "Represent Sri Lanka at international olympiads",
    ],
    milestones: [
      { icon: "bi-motherboard", text: "AICD Student Design Contest with IEEE CASS CEDA Sri Lanka: our first-ever IC tapeout" },
      { icon: "bi-cpu", text: "DICD Student Contest in Digital IC Design: launching 2027" },
      { icon: "bi-journal-text", text: "Research publications from learner projects, with mentors" },
      { icon: "bi-trophy", text: "Bronze Medal at the 20th International Microelectronics Olympiad" },
    ],
  },
  {
    key: "career",
    label: "Career",
    title: "Industry & Life-Ready",
    subtitle: "Skill Hive · Skill Reach",
    icon: "bi-briefcase",
    color: "#13a3a3",
    points: [
      "Connections between industry and students",
      "Soft skills alongside technical depth",
      "Learning that continues after the course",
    ],
    milestones: [
      { icon: "bi-building", text: "Build Your Own CPU graduates: qualified for an industry placement in Digital IC Design" },
      { icon: "bi-people", text: "FinishingSchool.LK: 3-week soft skills program, 350+ participants" },
      { icon: "bi-mic", text: "Guest lectures from industry in semiconductors, AI and computer architecture" },
      { icon: "bi-diagram-3", text: "Skill Hive: a platform linking industry and university students" },
      { icon: "bi-play-circle", text: "Keep learning anytime with on-demand courses on Learnova" },
    ],
  },
];

// Specialization tracks for the map: which programs sit in which stage.
export interface ITrack {
  name: string;
  icon: string;
  color: string;
  steps: {
    stage: string;
    programs?: string[];
    upcoming?: IUpcoming[];
    outcome?: string;
  }[];
}

export const tracks: ITrack[] = [
  {
    name: "Build Your Own CPU",
    icon: "bi-cpu",
    color: "#0089e5",
    steps: [
      { stage: "foundations", programs: ["/fundamentals-of-digital-system-design"] },
      { stage: "build", programs: ["/verilog-for-ASIC-FPGA-design-simulation"] },
      {
        stage: "advance",
        programs: ["/hicar", "/system-on-a-chip-design-workshop"],
        upcoming: [DAPDI],
      },
      { stage: "compete", outcome: "DICD Student Contest, launching 2027" },
    ],
  },
  {
    name: "Art of IC Design",
    icon: "bi-motherboard",
    color: "#7b5cd6",
    steps: [
      { stage: "discover", programs: ["/analog-cmos-ic-design"] },
      { stage: "foundations", programs: ["/cmos-ic-design"] },
      { stage: "compete", outcome: "AICD contest: first IC tapeout with IEEE CASS CEDA SL" },
    ],
  },
  {
    name: "Internet-of-Things",
    icon: "bi-wifi",
    color: "#1f9d6b",
    steps: [
      { stage: "foundations", programs: ["/microcontroller-based-embedded-system-design"] },
      {
        stage: "build",
        programs: [
          "/embedded-product-design",
          "/embedded-machine-learning-for-edge-computing",
        ],
        outcome: "Smart plugs delivered",
      },
    ],
  },
  {
    name: "Artificial Intelligence",
    icon: "bi-robot",
    color: "#f2683a",
    steps: [{ stage: "discover", programs: ["/introduction-to-generative-ai"] }],
  },
  {
    name: "Cybersecurity",
    icon: "bi-shield-lock",
    color: "#d9a21b",
    steps: [
      {
        stage: "foundations",
        programs: ["/cryptography-fundamentals-securing-the-digital-world"],
      },
    ],
  },
  {
    name: "Quantum Computing",
    icon: "bi-stars",
    color: "#13a3a3",
    steps: [{ stage: "discover", programs: ["/introduction-to-quantum-computing"] }],
  },
];

export const approach = [
  { icon: "bi-bullseye", text: "Start with the goal, then learn what it takes to build it" },
  { icon: "bi-hand-index-thumb", text: "Hands-on from day one, with instructors in real time" },
  { icon: "bi-layers", text: "Strong fundamentals before abstractions" },
  { icon: "bi-chat-heart", text: "Personalized feedback so no-one is left behind" },
  { icon: "bi-arrow-repeat", text: "Plan for months, deliver, improve and repeat" },
];

export const architecture = [
  { icon: "bi-broadcast", name: "Skill Cast", verb: "Inspire", text: "Workshops, tech talks, tutorials & podcasts" },
  { icon: "bi-mortarboard", name: "Skill Camp", verb: "Educate", text: "Deep-technology bootcamps" },
  { icon: "bi-diagram-3", name: "Skill Hive", verb: "Connect", text: "Industry and university students" },
  { icon: "bi-geo-alt", name: "Skill Reach", verb: "Extend", text: "Outreach across the country" },
  { icon: "bi-play-circle", name: "Learnova", verb: "Sustain", text: "On-demand learning, anytime" },
];

export const differentiators = [
  { icon: "bi-unlock", text: "Free entry points" },
  { icon: "bi-tools", text: "Hands-on experience" },
  { icon: "bi-building", text: "Industry & academic partners" },
  { icon: "bi-laptop", text: "Online, in English" },
  { icon: "bi-person-heart", text: "Learner-centred" },
];
